import type {DocumentNode} from "graphql";

import {sortFragments} from "./sort-fragments.ts";

/**
 * These symbols are used so we can create an opaque type, and so that we can
 * parameterize that type using custom fields that cannot be directly
 * referenced as folks deliberately won't have access to the symbols.
 *
 * See https://stackoverflow.com/a/56749647/23234
 */
declare const opaqueData: unique symbol;
declare const opaqueVariables: unique symbol;

// NOTE(kevinb): We need to include `TData` and `TVariables` in the type
// definition here, otherwise TypeScript ignores them when unifying type
// variables.
//
// NOTE(somewhatabstract): We don't make GqlOperation also be a DocumentNode
// because it may not always be a DocumentNode as we change our GraphQL
// solutions and so we want to explicitly switch between the types using our
// cast operators, giving us an easy place to implement conversion logic.
/**
 * A GraphQL query or mutation.
 */
export interface GqlOperation<TData, TVariables extends object = Empty> {
    [opaqueData]?: TData;
    [opaqueVariables]?: TVariables;
}

/**
 * Converts a `DocumentNode` into a parameterized `GqlOperation`.
 *
 * The fragment definitions in the returned `GqlOperation` are sorted.
 * See the `sortFragments` docs for context.
 */
export function gqlOp<TData, TVariables extends object = Empty>(
    documentNode: DocumentNode,
): GqlOperation<TData, TVariables> {
    /**
     * Ideally, TypeScript would support parameterized return types from tagged
     * string literals. That way we could just replace the use of graphql-tag
     * with our own function that called it (and possibly did something else
     * later). Sadly, TypeScript does not have a syntax for parameterized tagged
     * string literals that would allow us to do this, so we have to just wrap
     * the result of calls to `graphql-tag` using this `gqlOp` call instead.
     */
    return sortFragments(documentNode) as GqlOperation<TData, TVariables>;
}

/**
 * Unwraps the `DocumentNode` for a given `GqlOperation`.
 */
export function documentNode(d: GqlOperation<any, any>): DocumentNode {
    // NOTE: Using a cast to do this only works because we use `DocumentNode`
    // as the core type for `GqlOperation`.
    return d as DocumentNode;
}
